import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, Loader2Icon } from "lucide-react";
import { useAuthStore } from "@/lib/stores/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { t } from "@/lib/typography";
import { usePracticeLab, useActiveSession } from "@/lib/api/practice-labs";
import { useLabEvents } from "@/lib/hooks/use-lab-events";
import {
  usePracticeLabStore,
  useLabSnapshot,
  useLabConnection,
  useActiveStepState,
  useVerifiedProgress,
} from "@/lib/stores/practice-lab";
import {
  LabHeader,
  ConnectionPill,
  type PillMode,
  LabProgress,
  DefinitionOfDone,
  StepRail,
  StepPanel,
  CheckPanel,
  HintsDisclosure,
  LabControls,
  InstallCard,
  CompletionBanner,
  ReviewCard,
  Consolidation,
} from "@/components/practice-lab";
import type {
  PracticeLabResponse,
  PracticeLabSessionResponse,
  PracticeLabStepResponse,
} from "@/types/practice-lab";

// The most recent local check across all steps — drives the "last check Xs ago".
function latestCheckAt(snapshot: PracticeLabSessionResponse): string | undefined {
  let latest: string | undefined;
  for (const step of snapshot.steps) {
    if (step.last_check_at && (!latest || step.last_check_at > latest)) {
      latest = step.last_check_at;
    }
  }
  return latest;
}

function findStaticStep(
  lab: PracticeLabResponse,
  order: number | undefined
): PracticeLabStepResponse | undefined {
  if (order == null) return undefined;
  return lab.steps?.find((s) => s.order === order);
}

function Spinner() {
  return (
    <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
      <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
    </div>
  );
}

// The app's standard top-of-page back affordance (mirrors BookProgress). Labs
// are reached from the dashboard's Practice Labs section.
function BackToDashboard() {
  return (
    <Link
      to="/dashboard"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeftIcon className="size-4" />
      Back to Dashboard
    </Link>
  );
}

/**
 * Practice Lab page. The web surface is an OBSERVER of the CLI-driven flow: it
 * fetches the static lab, bootstraps the active session, opens the SSE stream,
 * and projects each full snapshot onto the page. It never runs checks or submits
 * — those happen in the member's terminal. Rendered inside the app's MainLayout.
 */
export function PracticeLab() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const labId = Number(id);

  const isAuthLoading = useAuthStore((s) => s.isLoading);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const labQuery = usePracticeLab(labId);
  const sessionQuery = useActiveSession(labId, {
    enabled: isAuthenticated && !!labId,
  });

  const setSnapshot = usePracticeLabStore((s) => s.setSnapshot);
  const reset = usePracticeLabStore((s) => s.reset);
  const snapshot = useLabSnapshot();
  const connection = useLabConnection();
  const activeStep = useActiveStepState();
  const progress = useVerifiedProgress();

  useLabEvents(labId || null, snapshot?.id ?? sessionQuery.data?.id ?? null);

  // Auth guard — same redirect pattern as the other authed pages.
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) navigate("/login");
  }, [isAuthLoading, isAuthenticated, navigate]);

  // Seed the store from the bootstrap fetch; SSE keeps it fresh afterwards. Only
  // seed a new/absent session so a background refetch can't clobber SSE.
  const fetchedSession = sessionQuery.data;
  useEffect(() => {
    if (fetchedSession && (!snapshot || snapshot.id !== fetchedSession.id)) {
      setSnapshot(fetchedSession);
    }
  }, [fetchedSession, snapshot, setSnapshot]);

  // Reset when leaving the page or switching labs.
  useEffect(() => reset, [labId, reset]);

  if (isAuthLoading || labQuery.isLoading) return <Spinner />;
  if (!isAuthenticated) return null; // redirecting

  if (labQuery.isError || !labQuery.data) {
    return (
      <div className="mx-auto max-w-md py-16">
        <Card>
          <CardContent className="space-y-3 py-4 text-center">
            <h2 className={t.sectionTitle}>Lab not found</h2>
            <p className="text-muted-foreground">
              This practice lab doesn't exist or you don't have access to it.
            </p>
            <Button asChild variant="outline">
              <Link to="/dashboard">Back to dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const lab = labQuery.data;

  if (sessionQuery.isLoading && !snapshot) return <Spinner />;

  // No active session → first-run / install (auto-connects when `lab start` runs).
  if (!snapshot) {
    return (
      <div className="space-y-6">
        <BackToDashboard />
        <LabHeader lab={lab} pill={<ConnectionPill mode="waiting" />} />
        <InstallCard slug={lab.slug} />
      </div>
    );
  }

  const isCompleted = snapshot.status === "completed";
  const pillMode: PillMode = isCompleted
    ? "torn-down"
    : connection === "error"
      ? "error"
      : "connected";

  return (
    <div className="space-y-6">
      <BackToDashboard />
      <LabHeader
        lab={lab}
        pill={<ConnectionPill mode={pillMode} lastCheckAt={latestCheckAt(snapshot)} />}
      />

      {isCompleted ? (
        <CompletedView lab={lab} snapshot={snapshot} progress={progress} />
      ) : (
        <InProgressView
          lab={lab}
          snapshot={snapshot}
          activeStep={activeStep}
          progress={progress}
        />
      )}
    </div>
  );
}

type ProgressView = { verified: number; total: number; percentage: number };

function InProgressView({
  lab,
  snapshot,
  activeStep,
  progress,
}: {
  lab: PracticeLabResponse;
  snapshot: PracticeLabSessionResponse;
  activeStep: ReturnType<typeof useActiveStepState>;
  progress: ProgressView;
}) {
  const staticStep = findStaticStep(lab, activeStep?.order);
  const stepLabel = activeStep
    ? `Step ${activeStep.order} of ${snapshot.step_count} — ${activeStep.title}`
    : `${snapshot.step_count} steps`;

  return (
    <div className="space-y-6">
      {staticStep?.definition_of_done && (
        <DefinitionOfDone text={staticStep.definition_of_done} />
      )}
      <LabProgress
        percentage={progress.percentage}
        label={stepLabel}
        verified={progress.verified}
        total={progress.total}
      />
      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)] lg:items-start">
        <StepRail steps={snapshot.steps} />
        <div className="min-w-0 space-y-6">
          {staticStep && <StepPanel step={staticStep} />}
          <CheckPanel specs={staticStep?.check_spec ?? []} state={activeStep} />
          <HintsDisclosure hints={staticStep?.hints ?? []} />
          <LabControls state={activeStep} />
        </div>
      </div>
    </div>
  );
}

function CompletedView({
  lab,
  snapshot,
  progress,
}: {
  lab: PracticeLabResponse;
  snapshot: PracticeLabSessionResponse;
  progress: ProgressView;
}) {
  // Show the final reviewed step's most recent review + its consolidation.
  const reviewedStep = [...snapshot.steps].reverse().find((s) => s.reviews.length > 0);
  const review = reviewedStep?.reviews[reviewedStep.reviews.length - 1];
  const consolidation = findStaticStep(lab, reviewedStep?.order)?.consolidation_md;

  return (
    <div className="space-y-6">
      <LabProgress
        percentage={progress.percentage || 100}
        label="All steps complete"
        verified={progress.verified}
        total={progress.total}
      />
      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)] lg:items-start">
        <StepRail steps={snapshot.steps} />
        <div className="min-w-0 space-y-6">
          <CompletionBanner title={`Lab complete — ${lab.title}`} />
          {review && reviewedStep && (
            <ReviewCard review={review} stepOrder={reviewedStep.order} />
          )}
          {consolidation && <Consolidation md={consolidation} />}
          <Button asChild variant="outline">
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
